import { toaster } from '@/components/ui/toaster';
import axiosInstance from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { PayoutBatchStatus } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isPayoutBatchConflict } from './payout-batch-conflict';

interface ContinuePayoutActionOptions {
  onSuccess?: (payoutId: string) => void;
  onError?: (error: Error) => void;
}

export const useContinuePayoutAction = (options?: ContinuePayoutActionOptions) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);

  const handleContinue = async (payoutId: string, currentStatus: PayoutBatchStatus) => {
    // Prevent multiple concurrent validations
    if (isValidating) return;

    setIsValidating(true);

    try {
      // First validation: Check if payout status is DRAFT
      if (currentStatus !== PayoutBatchStatus.DRAFT) {
        const errorMessage = getStatusErrorMessage(currentStatus);
        toaster.create({
          title: 'Cannot Continue Payout',
          description: errorMessage,
          type: 'error',
          duration: 5000,
        });
        options?.onError?.(new Error(`Invalid status: ${currentStatus}`));
        return;
      }

      // Second validation: Fetch payment details to ensure they exist and are valid
      const paymentDetailsResponse = await axiosInstance.get(
        `/dashboard/payouts/${payoutId}/payment-details`
      );
      const paymentDetails = paymentDetailsResponse.data;

      // Third validation: Check if payment intent is still valid
      if (!paymentDetails.paymentIntentClientSecret) {
        toaster.create({
          title: 'Payout Session Expired',
          description: 'The payment session has expired. Please create a new payout batch.',
          type: 'warning',
          duration: 7000,
        });
        options?.onError?.(new Error('Payment session expired'));
        return;
      }

      // All validations passed - navigate to checkout
      options?.onSuccess?.(payoutId);
      navigate(`/payout/${payoutId}/checkout`);
    } catch (error: unknown) {
      console.error('Error validating payout continuation:', error);

      // The payout was closed, paid or is being paid since the list was read:
      // say which, and read the list again so the row stops offering the action.
      if (isPayoutBatchConflict(error)) {
        toaster.create({
          title: t('payouts.continueBatch.error.title'),
          description: getApiErrorMessage(error, t),
          type: 'error',
          duration: 7000,
        });
        queryClient.invalidateQueries({ queryKey: ['payoutBatches'] });
        options?.onError?.(error instanceof Error ? error : new Error('Payout batch conflict'));
        return;
      }

      // Handle specific axios errors
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status: number } };
        const status = axiosError.response.status;
        if (status === 404) {
          toaster.create({
            title: 'Payout Not Found',
            description: "This payout no longer exists or you don't have access to it.",
            type: 'error',
            duration: 5000,
          });
          options?.onError?.(new Error('Payout not found'));
          return;
        }

        if (status === 401) {
          toaster.create({
            title: 'Access Denied',
            description: "You don't have permission to continue this payout.",
            type: 'error',
            duration: 5000,
          });
          options?.onError?.(new Error('Unauthorized access'));
          return;
        }
      }

      // Handle network errors
      toaster.create({
        title: 'Connection Issue',
        description: 'Please check your connection and try again.',
        type: 'error',
        duration: 5000,
      });

      options?.onError?.(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      setIsValidating(false);
    }
  };

  return {
    handleContinue,
    isValidating,
  };
};

// Helper function to provide specific error messages based on payout status
const getStatusErrorMessage = (status: PayoutBatchStatus): string => {
  switch (status) {
    case PayoutBatchStatus.PROCESSING:
      return 'This payout is currently being processed and cannot be modified.';
    case PayoutBatchStatus.COMPLETED:
      return 'This payout has already been completed successfully.';
    case PayoutBatchStatus.PARTIALLY_COMPLETED:
      return 'This payout is partially completed and requires review.';
    case PayoutBatchStatus.FAILED:
      return 'This payout has failed. Please create a new payout batch.';
    case PayoutBatchStatus.PENDING:
      return 'This payout is already pending payment and cannot be continued.';
    default:
      return 'This payout is no longer in draft status and cannot be continued.';
  }
};
